import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, message } from 'antd';

import { getIsAdmin } from '../../utils/isAdmin';
import { EdicoesCatalog } from './EdicoesCatalog';
import { CategoriasCatalog } from './CategoriasCatalog';
import { TemasCatalog } from './TemasCatalog';
import { DestaqueCatalog } from './DestaqueCatalog';
import { TorneiosCatalog } from './TorneiosCatalog';
import { DesafiosCatalog } from './DesafiosCatalog';
import { TreinosCatalog } from './TreinosCatalog';
import { RecompensasCatalog } from './RecompensasCatalog';
import { JogosTab } from './catalog/JogosTab';
import { NewGameWizard } from './catalog/NewGameWizard';

const { TabPane } = Tabs;

/**
 * Painel administrativo do portal. Nove abas em três grupos:
 * Conteúdo (Edições · Temas · Jogos · Destaque), Programação (Desafios · Treinos · Torneios) e
 * Configurações (Categorias · Recompensas). A aba Jogos é o catálogo unificado
 * (cruzadas + jogos) e abre o assistente "+ Novo jogo"; a criação por gerador/import/editor
 * vive nesse assistente. Desafios e Recompensas estão desabilitadas (o app mobile deixou de
 * ter desafio diário e baús); o código dessas abas fica no repo para reativação futura.
 * A aba inicial vem de `?tab=` (ex.: `/admin?tab=jogos`); valor desconhecido ou desabilitado
 * cai em Edições.
 *
 * Trava de admin com getIsAdmin() (o backend também bloqueia com 403).
 */
const TAB_KEYS = [
  'edicoes',
  'temas',
  'jogos',
  'destaque',
  'desafios',
  'treinos',
  'torneios',
  'categorias',
  'recompensas',
];

/** Abas visíveis mas não clicáveis (ver comentário do componente). */
export const DISABLED_TABS: readonly string[] = ['desafios', 'recompensas'];

const GROUP_BY_FIRST_TAB: Record<string, string> = {
  edicoes: 'Conteúdo',
  desafios: 'Programação',
  categorias: 'Configurações',
};

const tabLabel = (key: string, name: string) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2 }}>
    <small
      aria-hidden
      style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.55, minHeight: 12 }}
    >
      {GROUP_BY_FIRST_TAB[key] ?? ''}
    </small>
    <span data-tab-name>{name}</span>
  </span>
);

export const Admin: React.FC = () => {
  const navigate = useNavigate();
  const admin = getIsAdmin();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? '';
  const [activeTab, setActiveTab] = useState(
    TAB_KEYS.includes(requestedTab) && !DISABLED_TABS.includes(requestedTab)
      ? requestedTab
      : 'edicoes',
  );
  const [wizardOpen, setWizardOpen] = useState(false);
  const [jogosReloadKey, setJogosReloadKey] = useState(0);

  useEffect(() => {
    if (!admin) {
      message.error('Acesso restrito a administradores.');
      navigate('/');
    }
  }, [admin, navigate]);

  if (!admin) return null;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: 24 }}>
      <h1>Painel administrativo</h1>
      <p>
        <Link to="/">← voltar ao início</Link>
      </p>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={tabLabel('edicoes', 'Edições')} key="edicoes">
          <EdicoesCatalog />
        </TabPane>
        <TabPane tab={tabLabel('temas', 'Temas')} key="temas">
          <TemasCatalog />
        </TabPane>
        <TabPane tab={tabLabel('jogos', 'Jogos')} key="jogos">
          <JogosTab onNewGame={() => setWizardOpen(true)} reloadKey={jogosReloadKey} />
        </TabPane>
        <TabPane tab={tabLabel('destaque', 'Destaque')} key="destaque">
          <DestaqueCatalog active={activeTab === 'destaque'} />
        </TabPane>
        <TabPane tab={tabLabel('desafios', 'Desafios')} key="desafios" disabled={DISABLED_TABS.includes('desafios')}>
          <DesafiosCatalog />
        </TabPane>
        <TabPane tab={tabLabel('treinos', 'Treinos')} key="treinos">
          <TreinosCatalog />
        </TabPane>
        <TabPane tab={tabLabel('torneios', 'Torneios')} key="torneios">
          <TorneiosCatalog />
        </TabPane>
        <TabPane tab={tabLabel('categorias', 'Categorias')} key="categorias">
          <CategoriasCatalog active={activeTab === 'categorias'} />
        </TabPane>
        <TabPane tab={tabLabel('recompensas', 'Recompensas')} key="recompensas" disabled={DISABLED_TABS.includes('recompensas')}>
          <RecompensasCatalog />
        </TabPane>
      </Tabs>

      <NewGameWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => setJogosReloadKey((k) => k + 1)}
      />
    </div>
  );
};

export default Admin;
